"use client"

import React from 'react'

type CountUpProps = {
  end: number
  duration?: number // in seconds
  prefix?: string
  suffix?: string
  decimals?: number // number of decimal places to show
  compact?: boolean // use compact notation (e.g., 2.5K, 1.2M)
  className?: string
}

export default function CountUp({
  end,
  duration = 1.6,
  prefix = '',
  suffix = '',
  decimals = 0,
  compact = true,
  className,
}: CountUpProps) {
  const ref = React.useRef<HTMLSpanElement>(null)
  const [display, setDisplay] = React.useState<string>('0')
  const [started, setStarted] = React.useState(false)

  const format = React.useMemo(() => {
    const opts: Intl.NumberFormatOptions = compact
      ? { notation: 'compact', maximumFractionDigits: decimals }
      : { maximumFractionDigits: decimals, minimumFractionDigits: decimals }
    const formatter = new Intl.NumberFormat(undefined, opts)
    return (n: number) => `${prefix}${formatter.format(n)}${suffix}`
  }, [compact, decimals, prefix, suffix])

  React.useEffect(() => {
    const node = ref.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && !started) {
          setStarted(true)
        }
      },
      { threshold: 0.2 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [started])

  React.useEffect(() => {
    if (!started) return
    let raf = 0
    const start = performance.now()
    const total = Math.max(0.2, duration) * 1000

    const animate = (now: number) => {
      const t = Math.min(1, (now - start) / total)
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3)
      const current = end * eased
      setDisplay(format(current))
      if (t < 1) {
        raf = requestAnimationFrame(animate)
      } else {
        setDisplay(format(end))
      }
    }
    raf = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(raf)
  }, [started, end, duration, format])

  return (
    <span ref={ref} className={className} aria-label={`${prefix}${end}${suffix}`}>
      {display}
    </span>
  )
}
